import { PlusOutlined } from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from 'antd';
import React, { useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ConcanateSortable from './ConcanateSortable';

const Concatenate = ({ value, onChange }) => {
  // value shape: { fields: [...], delimiter: ' ' }
  // fields: [{ type: 'attribute'|'freetext', value: '...', id: '...' }]

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Track previous fields to detect when new data is loaded
  const previousFieldsRef = useRef(value?.fields);

  // Normalize items: ensure each field has an id for drag and drop
  // When data is loaded from backend, fields may not have ids
  const items = useMemo(() => {
    const fields = value?.fields || [];
    return fields.map((field) => {
      // If field already has an id, keep it; otherwise generate one
      if (field.id) {
        return field;
      }
      return {
        ...field,
        id: uuidv4(),
      };
    });
  }, [value?.fields]);

  // TODO: optim this with useMemo/lodash
  // Sync normalized data back to parent if IDs were added
  // This ensures IDs are persisted when data is loaded from backend
  useEffect(() => {
    const currentFields = value?.fields || [];
    const previousFields = previousFieldsRef.current || [];

    // Check if fields have actually changed (new data loaded)
    const fieldsChanged =
      currentFields.length !== previousFields.length ||
      currentFields.some(
        (field, index) =>
          !previousFields[index] ||
          field.type !== previousFields[index]?.type ||
          field.value !== previousFields[index]?.value ||
          !field.id // Also trigger if current field is missing ID
      );

    // If fields changed and some are missing IDs, normalize and sync
    if (fieldsChanged) {
      const hasMissingIds = currentFields.some((field) => !field.id);

      if (hasMissingIds) {
        const normalizedFields = currentFields.map((field) => {
          if (field.id) {
            return field;
          }
          return {
            ...field,
            id: uuidv4(),
          };
        });

        // Update parent with normalized fields
        onChange({
          ...value,
          fields: normalizedFields,
        });
      }
    }

    previousFieldsRef.current = currentFields;
  }, [value?.fields]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return; // Invalid indices, skip update
      }

      const newItems = arrayMove(items, oldIndex, newIndex);
      onChange({
        ...value,
        fields: newItems,
      });
    }
  };

  const handleAddItem = () => {
    const newItem = { id: uuidv4(), type: 'attribute', value: '' };
    const newFields = [...items, newItem];
    onChange({
      ...value,
      fields: newFields,
    });
  };

  const handleDeleteItem = (id) => {
    const newFields = items.filter((item) => item.id !== id);
    onChange({
      ...value,
      fields: newFields,
    });
  };

  const handleTypeChange = (id, newType) => {
    const newFields = items.map((item) =>
      item.id === id ? { ...item, type: newType, value: '' } : item
    );
    onChange({
      ...value,
      fields: newFields,
    });
  };

  const handleValueChange = (id, newValue) => {
    const newFields = items.map((item) =>
      item.id === id ? { ...item, value: newValue } : item
    );
    onChange({
      ...value,
      fields: newFields,
    });
  };

  return (
    <div>
      <div style={{ margin: '8px 0' }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>
          Éléments à concaténer
        </span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((item) => (
              <ConcanateSortable
                key={item.id}
                id={item.id}
                item={item}
                onTypeChange={handleTypeChange}
                onValueChange={handleValueChange}
                onDelete={handleDeleteItem}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button
        type="dashed"
        onClick={handleAddItem}
        style={{ width: '100%', marginTop: 16 }}
        icon={<PlusOutlined />}
      >
        Ajouter un élément
      </Button>
    </div>
  );
};

export default Concatenate;
